import React from 'react';
import './Results.css';

function Results({ results, priorYearData, uploadedFile, onStartOver }) {
  return (
    <div className="results-container">
      <h2>Step 3: Your Tax Filing Recommendations</h2>

      {results.maritalStatus === 'Married' && results.recommendation && (
        <div className="recommendation-card">
          <div className="recommendation-header">
            <h3>Filing Status Recommendation</h3>
            <div className={`recommendation-badge ${results.recommendation.toLowerCase()}`}>
              {results.recommendation}
            </div>
          </div>

          <div className="recommendation-content">
            <h4>Recommended Filing Status: {results.recommendation}</h4>
            <p className="reasoning">{results.reasoning}</p>
            {(!results.spousesData || results.spousesData.length < 2) && (
              <p className="note" style={{marginTop: '10px', padding: '10px', backgroundColor: '#f0f8ff', borderLeft: '4px solid #2196F3', fontSize: '0.9em'}}>
                <strong>Note:</strong> For a more precise recommendation, consider uploading your spouse's prior year tax returns to compare income levels and filing scenarios.
              </p>
            )}

            <div className="filing-comparison">
              <h4>Filing Status Comparison:</h4>
              <div className="comparison-grid">
                <div className={`status-card ${results.recommendation === 'MFJ' ? 'recommended' : ''}`}>
                  <h5>Married Filing Jointly (MFJ)</h5>
                  <ul>
                    <li>Larger standard deduction</li>
                    <li>Wider tax brackets</li>
                    <li>Access to more credits and deductions</li>
                    <li>Must report joint income</li>
                  </ul>
                </div>
                <div className={`status-card ${results.recommendation === 'MFS' ? 'recommended' : ''}`}>
                  <h5>Married Filing Separately (MFS)</h5>
                  <ul>
                    <li>Income segregation possible</li>
                    <li>Individual liability limits</li>
                    <li>Beneficial if one spouse has high expenses</li>
                    <li>Reduced or no access to some credits</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {results.maritalStatus !== 'Married' && (
        <div className="info-card">
          <h4>Filing Status: {results.maritalStatus}</h4>
          <p>
            Your filing status appears to be {results.maritalStatus}. The MFJ/MFS comparison
            only applies to married taxpayers.
          </p>
        </div>
      )}

      {results.maritalStatus === 'Married' && results.priorYearSummary && (
        <div className="info-card">
          <h4>Prior Year Filing (Detected)</h4>
          <p>
            <strong>Detected mode:</strong> {results.priorYearSummary.mode}
          </p>
          <p>
            <strong>Spouses included:</strong> {results.priorYearSummary.spousesIncluded}
          </p>
          <p>
            <strong>Combined income (detected):</strong> ${Number(results.priorYearSummary.totalIncome || 0).toLocaleString()}
          </p>
          <p>
            <strong>Combined tax paid (detected):</strong> ${Number(results.priorYearSummary.totalTaxPaid || 0).toLocaleString()}
          </p>
          <p style={{ marginTop: '10px', fontSize: '0.9em' }}>
            <strong>Note:</strong> This reflects what was detected on the uploaded prior-year returns (MFJ vs MFS), not a full
            recalculation of what would have been optimal.
          </p>
        </div>
      )}

      {results.maritalStatus === 'Married' && results.priorYearMfJMfsEstimate && (
        <div className="info-card">
          <h4>Prior Year MFJ vs MFS (Estimate)</h4>
          <p>
            <strong>MFJ estimated federal income tax:</strong> ${Number(results.priorYearMfJMfsEstimate.mfj?.estimatedFederalIncomeTax || 0).toLocaleString()}
          </p>
          <p>
            <strong>MFS estimated federal income tax (combined):</strong> ${Number(results.priorYearMfJMfsEstimate.mfs?.estimatedFederalIncomeTaxCombined || 0).toLocaleString()}
          </p>
          <p>
            <strong>Difference (MFS − MFJ):</strong> ${Number(results.priorYearMfJMfsEstimate.difference?.mfsMinusMfjEstimatedTax || 0).toLocaleString()}
          </p>
          <p>
            <strong>Interpretation:</strong> {results.priorYearMfJMfsEstimate.difference?.interpretation}
          </p>
          <p style={{ marginTop: '10px', fontSize: '0.9em' }}>
            <strong>Important:</strong> {results.priorYearMfJMfsEstimate.notes}
          </p>
        </div>
      )}

      <div className="summary-grid">
        <div className="summary-card">
          <h4>Your Situation</h4>
          <ul className="summary-list">
            <li><strong>Marital Status:</strong> {results.maritalStatus}</li>
            <li><strong>Has Investments:</strong> {results.has_investments ? 'Yes' : 'No'}</li>
            <li><strong>Has Properties:</strong> {results.has_properties ? 'Yes' : 'No'}</li>
            <li><strong>Mortgage Interest:</strong> {results.has_mortgage_interest ? 'Yes' : 'No'}</li>
            <li><strong>Student Loan Interest:</strong> {results.has_student_loan_interest ? 'Yes' : 'No'}</li>
            <li><strong>Dependents:</strong> {results.has_dependents ? 'Yes' : 'No'}</li>
            <li><strong>Itemizes Deductions:</strong> {results.itemizes_deductions ? 'Yes' : 'No'}</li>
            <li><strong>Wants Liability Separation:</strong> {results.wants_liability_separation ? 'Yes' : 'No'}</li>
          </ul>
        </div>

        {priorYearData?.primary && (
          <div className="summary-card">
            <h4>Prior Year Information</h4>
            <ul className="summary-list">
              <li><strong>Primary Filer - Filing Status:</strong> {priorYearData.primary?.federal?.filingStatus || priorYearData.primary?.state?.filingStatus || 'Not detected'}</li>
              {(priorYearData.primary?.federal?.totalIncome > 0 || priorYearData.primary?.state?.totalIncome > 0) && (
                <li><strong>Primary Filer - Total Income:</strong> ${Math.max(priorYearData.primary?.federal?.totalIncome || 0, priorYearData.primary?.state?.totalIncome || 0).toLocaleString()}</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Spouse Comparison Card */}
      {results.spousesData && results.spousesData.length === 2 && (
        <div className="spouse-comparison-card">
          <h4>Spouse Income Comparison</h4>
          <div className="spouse-comparison-grid">
            <div className="spouse-income-card">
              <h5>{results.spousesData[0]?.spouse_name || 'Spouse 1'}</h5>
              <div className="income-amount">${results.spousesData[0]?.total_income.toLocaleString()}</div>
              <p className="income-label">Gross Income</p>
            </div>
            <div className="vs-symbol">vs</div>
            <div className="spouse-income-card">
              <h5>{results.spousesData[1]?.spouse_name || 'Spouse 2'}</h5>
              <div className="income-amount">${results.spousesData[1]?.total_income.toLocaleString()}</div>
              <p className="income-label">Gross Income</p>
            </div>
          </div>
          <div className="combined-income">
            <p><strong>Combined Income:</strong> ${(results.spousesData[0]?.total_income + results.spousesData[1]?.total_income).toLocaleString()}</p>
            <p className="income-disparity">
              <strong>Income Disparity:</strong> {(Math.max(results.spousesData[0]?.total_income, results.spousesData[1]?.total_income) / Math.min(results.spousesData[0]?.total_income, results.spousesData[1]?.total_income || 1)).toFixed(2)}x
            </p>
          </div>
        </div>
      )}

      {priorYearData?.spouse && (
        <div className="spouse-prior-year-card">
          <h4>Spouse's Prior Year Information</h4>
          <ul className="summary-list">
            <li><strong>Filing Status:</strong> {priorYearData.spouse?.federal?.filingStatus || priorYearData.spouse?.state?.filingStatus || 'Not detected'}</li>
            {(priorYearData.spouse?.federal?.totalIncome > 0 || priorYearData.spouse?.state?.totalIncome > 0) && (
              <li><strong>Total Income:</strong> ${Math.max(priorYearData.spouse?.federal?.totalIncome || 0, priorYearData.spouse?.state?.totalIncome || 0).toLocaleString()}</li>
            )}
          </ul>
        </div>
      )}

      <div className="important-note">
        <h4>⚠️ Important Notice</h4>
        <p>
          This assessment is for informational purposes only and should not be considered
          professional tax advice. Tax situations can be complex, and individual circumstances
          vary significantly. We strongly recommend consulting with a qualified tax professional
          or CPA before making any filing decisions, especially if you have:
        </p>
        <ul>
          <li>Investment income or capital gains</li>
          <li>Rental properties</li>
          <li>Self-employment income</li>
          <li>Significant deductions or credits</li>
          <li>Complex financial situations</li>
        </ul>
      </div>

      <div className="action-buttons">
        <button onClick={onStartOver} className="button primary">
          Analyze Another Return
        </button>
      </div>
    </div>
  );
}

export default Results;
